import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon, IconName } from '@tviklax/ui-components';
import { Contact } from '../../../shared/types/contact';
import { ContactCard } from '../ContactCard/ContactCard';

interface ContactPanelProps {
  title: string;
  icon?: IconName;
  contacts: (Contact | undefined)[];
  onContactAction?: (contact: Contact, action: 'message' | 'view-history') => void;
}

const Container = styled.div`
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.surface};
  border: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${({ theme }) => theme.colors.gray100};
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  flex: 1;
  text-align: left;
`;

const ChevronIcon = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  transition: transform 0.2s;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Content = styled.div<{ $isOpen: boolean }>`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[4]};
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  text-align: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 13px;
`;

export const ContactPanel: React.FC<ContactPanelProps> = ({
  title,
  icon,
  contacts,
  onContactAction,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const validContacts = contacts.filter((c): c is Contact => !!c);
  const hasContacts = validContacts.length > 0;

  return (
    <Container>
      <Header onClick={() => setIsOpen(!isOpen)}>
        {icon && <Icon name={icon} size={16} />}
        <Title>{title}</Title>
        {hasContacts && (
          <span style={{ fontSize: '12px', color: '#64748b', marginRight: '8px' }}>
            {validContacts.length}
          </span>
        )}
        <ChevronIcon $isOpen={isOpen}>
          <Icon name="ChevronDown" size={16} />
        </ChevronIcon>
      </Header>

      <Content $isOpen={isOpen}>
        {validContacts.length > 0 ? (
          <ContactList>
            {validContacts.map((contact) => (
              <ContactCard
                key={contact.id}
                contact={contact}
                onAction={onContactAction 
                  ? (action) => onContactAction(contact, action)
                  : undefined}
              />
            ))}
          </ContactList>
        ) : (
          <EmptyState>No contacts assigned</EmptyState>
        )}
      </Content>
    </Container>
  );
};
